import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const getStoredTheme = () => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('theme') || 'system';
  }
  return 'system';
};

export const ThemeProvider = ({ children }) => {
  // Initial state is deterministic (matches prerender output) to avoid
  // hydration mismatches. Real theme is read from localStorage and the
  // matchMedia preference in a post-mount effect below.
  const [theme, setThemeState] = useState('system');
  const [systemTheme, setSystemTheme] = useState('light');

  // Calculate resolved theme based on user preference or system
  const resolvedTheme = theme === 'system' ? systemTheme : theme;
  const isDark = resolvedTheme === 'dark';

  // Read stored theme + initial system preference after hydration
  useEffect(() => {
    const stored = getStoredTheme();
    if (stored !== 'system') setThemeState(stored);
    setSystemTheme(getSystemTheme());
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDark) {
      root.classList.remove('light');
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#0f172a' : '#ffffff');
    }
  }, [isDark]);

  // Store theme preference
  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  const setTheme = useCallback((newTheme) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    // Cycle through: light -> dark -> system -> light
    setThemeState((current) => {
      if (current === 'light') return 'dark';
      if (current === 'dark') return 'system';
      return 'light';
    });
  }, []);

  // Simple toggle between light and dark (ignoring system)
  const toggleLightDark = useCallback(() => {
    setThemeState((current) => {
      const currentResolved = current === 'system' ? systemTheme : current;
      return currentResolved === 'light' ? 'dark' : 'light';
    });
  }, [systemTheme]);

  const value = {
    theme,           // User preference: 'light', 'dark', or 'system'
    resolvedTheme,   // Actual theme being applied: 'light' or 'dark'
    isDark,          // Boolean for convenience
    isSystem: theme === 'system',
    setTheme,        // Set specific theme
    toggleTheme,     // Cycle through all options
    toggleLightDark, // Simple light/dark toggle
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;